"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";

import { cn } from "@/lib/utils";
import { VerticalScrollEndShadow } from "@/components/ui/scroll-overlap";

type OverlayScrollbarProps = {
  className?: string;
  viewportClassName?: string;
  viewportStyle?: CSSProperties;
  /** Element rendered as the scrolling viewport — pass "aside"/"section"
   *  etc. (with `viewportProps` aria attributes) to keep landmark semantics
   *  when this replaces a semantic scroll container. */
  viewportAs?: "div" | "aside" | "section";
  /** Extra props (aria-label, wheel handlers, …) applied to the scrolling
   *  viewport element itself — NOT the outer wrapper, which never scrolls. */
  viewportProps?: React.HTMLAttributes<HTMLElement>;
  children: ReactNode;
  /** Inset (px) of the thumb track from the top/bottom edges. */
  trackInset?: number;
  /** Minimum thumb height (px). */
  minThumb?: number;
  /** Delay (ms) before the thumb fades out after scrolling stops. */
  hideDelay?: number;
  /** Adds a dark bottom-edge shadow while more content remains below. */
  endShadow?: boolean;
  /** Reports the bottom-edge shadow opacity for a shadow rendered by an ancestor. */
  onEndShadowOpacityChange?: (opacity: number) => void;
  fadeHeight?: number;
  fadeDistance?: number;
};

type ThumbState = {
  height: number;
  top: number;
  scrollable: boolean;
};

/**
 * Scroll container whose scrollbar overlays the content instead of reserving a
 * layout gutter. The native scrollbar is hidden, so child rows keep their full
 * width (hover/selected fills don't shrink), while a thin custom thumb is drawn
 * on top of the right edge.
 */
export function OverlayScrollbar({
  className,
  viewportClassName,
  viewportStyle,
  viewportAs: Viewport = "div",
  viewportProps,
  children,
  trackInset = 4,
  minThumb = 24,
  hideDelay = 900,
  endShadow = false,
  onEndShadowOpacityChange,
  fadeHeight = 48,
  fadeDistance = 48,
}: OverlayScrollbarProps) {
  const viewportRef = useRef<HTMLElement | null>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dragStateRef = useRef<{
    startY: number;
    startScrollTop: number;
    maxScrollTop: number;
    maxThumbTop: number;
  } | null>(null);
  const dragCleanupRef = useRef<(() => void) | null>(null);

  const [thumb, setThumb] = useState<ThumbState>({
    height: 0,
    top: 0,
    scrollable: false,
  });
  const [active, setActive] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [fadeOpacity, setFadeOpacity] = useState(0);

  const measure = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;

    const { clientHeight, scrollHeight, scrollTop } = el;
    const scrollable = scrollHeight > clientHeight + 1;
    if (!scrollable) {
      setThumb({ height: 0, top: 0, scrollable: false });
      setFadeOpacity(0);
      onEndShadowOpacityChange?.(0);
      return;
    }

    const distanceFromBottom = scrollHeight - clientHeight - scrollTop;
    const nextFadeOpacity = Math.max(
      0,
      Math.min(1, distanceFromBottom / fadeDistance),
    );
    setFadeOpacity(nextFadeOpacity);
    onEndShadowOpacityChange?.(nextFadeOpacity);

    const trackHeight = Math.max(0, clientHeight - trackInset * 2);
    if (trackHeight <= 0) {
      setThumb({ height: 0, top: 0, scrollable: false });
      return;
    }

    const thumbHeight = Math.min(
      trackHeight,
      Math.max(
        minThumb,
        Math.round(trackHeight * (clientHeight / scrollHeight)),
      ),
    );
    const maxScrollTop = scrollHeight - clientHeight;
    const maxThumbTop = Math.max(0, trackHeight - thumbHeight);
    const top =
      trackInset +
      (maxScrollTop > 0 && maxThumbTop > 0
        ? (scrollTop / maxScrollTop) * maxThumbTop
        : 0);

    setThumb({ height: thumbHeight, top, scrollable: true });
  }, [fadeDistance, minThumb, onEndShadowOpacityChange, trackInset]);

  const flashActive = useCallback(() => {
    setActive(true);
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    hideTimeoutRef.current = setTimeout(() => {
      setActive(false);
      hideTimeoutRef.current = null;
    }, hideDelay);
  }, [hideDelay]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;

    const onScroll = () => {
      measure();
      flashActive();
    };

    const ro = new ResizeObserver(measure);
    const observeChildren = () => {
      ro.disconnect();
      ro.observe(el);
      for (const child of Array.from(el.children)) ro.observe(child);
    };
    const mo = new MutationObserver(() => {
      observeChildren();
      measure();
    });
    mo.observe(el, { childList: true });

    observeChildren();
    measure();
    el.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      el.removeEventListener("scroll", onScroll);
      ro.disconnect();
      mo.disconnect();
      dragCleanupRef.current?.();
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, [measure, flashActive]);

  const onThumbPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const el = viewportRef.current;
      if (!el) return;
      event.preventDefault();

      const trackHeight = Math.max(0, el.clientHeight - trackInset * 2);
      dragStateRef.current = {
        startY: event.clientY,
        startScrollTop: el.scrollTop,
        maxScrollTop: el.scrollHeight - el.clientHeight,
        maxThumbTop: Math.max(0, trackHeight - thumb.height),
      };
      setDragging(true);

      const onMove = (moveEvent: PointerEvent) => {
        const drag = dragStateRef.current;
        const viewport = viewportRef.current;
        if (!drag || !viewport || drag.maxThumbTop <= 0) return;
        const deltaY = moveEvent.clientY - drag.startY;
        const scrollDelta = (deltaY / drag.maxThumbTop) * drag.maxScrollTop;
        viewport.scrollTop = drag.startScrollTop + scrollDelta;
      };
      const detach = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
        dragStateRef.current = null;
        dragCleanupRef.current = null;
      };
      const onUp = () => {
        detach();
        setDragging(false);
      };

      // Capture the pointer so the drag keeps tracking outside the thumb,
      // and treat pointercancel (touch interruption, OS gesture) like
      // pointerup — otherwise the drag state sticks until the next click.
      event.currentTarget.setPointerCapture?.(event.pointerId);
      dragCleanupRef.current = detach;
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    },
    [thumb.height, trackInset],
  );

  const visible = thumb.scrollable && (active || hovered || dragging);

  return (
    <div
      className={cn("relative", className)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Viewport
        {...viewportProps}
        ref={viewportRef as React.Ref<never>}
        style={{ ...viewportProps?.style, ...viewportStyle }}
        className={cn(
          "scrollbar-hidden h-full overflow-y-auto",
          viewportClassName,
          viewportProps?.className,
        )}
      >
        {children}
      </Viewport>

      {endShadow ? (
        <VerticalScrollEndShadow height={fadeHeight} opacity={fadeOpacity} />
      ) : null}

      {thumb.scrollable ? (
        <div
          role="presentation"
          onPointerDown={onThumbPointerDown}
          className={cn(
            "absolute right-0.5 z-10 w-1.5 cursor-pointer rounded-full transition-[background-color,opacity] duration-200 hover:bg-neutral-07",
            dragging ? "bg-neutral-08" : "bg-neutral-05",
            visible ? "opacity-100" : "opacity-0",
          )}
          style={{
            top: thumb.top,
            height: thumb.height,
          }}
        />
      ) : null}
    </div>
  );
}
